// Handwriting is only readable by the AI if enough pixels reach it.
// Measured on real submissions: a full page captured at 480x640 (the
// browser's default webcam size) is unreadable — the model invents a
// plausible answer instead, differently on every run. The same page
// at a few megapixels is read back exactly, identically, twice in a
// row. So: capture as large as the device allows, then cap the long
// edge here — beyond this the extra bytes buy no extra accuracy and
// only push the request toward the body-size limit.
export const MAX_SUBMISSION_IMAGE_EDGE = 2048;
const SUBMISSION_IMAGE_QUALITY = 0.92;

// Ask for far more than any phone will give; the browser negotiates
// down to the best the camera actually supports. Kept as `ideal` so a
// device that cannot deliver it still opens the camera instead of
// failing outright.
export const CAMERA_VIDEO_CONSTRAINTS = {
  facingMode: { ideal: "environment" },
  width: { ideal: 3840 },
  height: { ideal: 2160 },
};

async function loadDecodedImage(source) {
  if (typeof createImageBitmap === "function") {
    try {
      // "from-image" applies the EXIF rotation phone photos carry, so
      // a picture that looks upright in the gallery is not sent to the
      // model on its side.
      return { image: await createImageBitmap(source, { imageOrientation: "from-image" }), release: null };
    } catch {
      // Older browsers reject the options argument — fall through.
    }
  }

  const url = URL.createObjectURL(source);
  const image = new Image();
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = () => reject(new Error("unreadable image"));
    image.src = url;
  });
  return { image, release: () => URL.revokeObjectURL(url) };
}

function canvasToJpegBlob(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", SUBMISSION_IMAGE_QUALITY);
  });
}

/**
 * Draws a source (video frame, image bitmap) into a JPEG blob whose
 * long edge is at most MAX_SUBMISSION_IMAGE_EDGE.
 */
function drawScaled(source, width, height) {
  const scale = Math.min(1, MAX_SUBMISSION_IMAGE_EDGE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvasToJpegBlob(canvas);
}

export function captureVideoFrame(video) {
  return drawScaled(video, video.videoWidth, video.videoHeight);
}

/**
 * Normalizes a gallery-picked file: EXIF rotation applied, capped to
 * the same long edge as camera captures, re-encoded as JPEG (which
 * also rescues iPhone HEIC files the API cannot read).
 */
export async function prepareSubmissionFile(file) {
  const { image, release } = await loadDecodedImage(file);
  try {
    const blob = await drawScaled(
      image,
      image.width || image.naturalWidth,
      image.height || image.naturalHeight
    );
    if (image.close) image.close();
    return blob || file;
  } finally {
    if (release) release();
  }
}
